import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import {
  Box,
  Button,
  Container,
  TextField,
  Checkbox,
  Link,
  MenuItem,
  FormControl,
  ListSubheader,
  Select,
} from '@mui/material'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import validationSchema from 'ui/shorthands/validationSchema'
import { Layout } from 'ui/layout/Layout'
import { AppBarCommon } from 'ui/partials/AppBar'
import { StackItem } from 'ui/layout/StackItem'
import { H2, Text2 } from 'ui/shorthands/Texts'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import {
  StackItemTextField,
  useStyles as textFieldStyles,
} from 'ui/shorthands/StackItemTextField'
import Confirmation from 'ui/partials/Confirmation'
import information from 'config/information'
import placeholder from 'config/placeholder'
import type {
  partnerCompanyProps,
  ApplicationEntryFormProps,
  EntryPlanCategory,
} from 'types/med'
import Modal from 'ui/layout/Modal'
import { ConfirmContent, SendingContent } from 'ui/partials/ConfirmContent'
import FormHelperText from '@mui/material/FormHelperText'
import { ApiErrorWithMessage } from 'app/client'
import MedAddressInputForm from 'ui/organisms/MedAddressInputForm'
import SwitchRadioButton from 'ui/organisms/SwitchRadioButton'

export const applicationEntryStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
  },
  header: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  category: {
    backgroundColor: theme.palette.background.paper,
  },
  firstInput: {
    paddingRight: theme.spacing(2),
  },
  submitWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '32px 16px 90px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  submitButton: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  topCheckbox: {
    paddingLeft: 0,
  },
  text: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
  initialSupportEmail: {
    width: '20em',
    maxWidth: '100%',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(2, 0, 0, 0),
  },
  formControl: {
    minWidth: 440,
  },
  isRequestSwitch: {
    paddingTop: theme.spacing(1),
  },
  planDescription: {
    paddingTop: theme.spacing(2),
  },
}))

export type ApplicationEntryProps = {
  partnerCompanies?: Array<partnerCompanyProps>
  entryPlans: EntryPlanCategory[]
  defaultValues?: ApplicationEntryFormProps
  onSubmit: (data: ApplicationEntryFormProps) => Promise<void>
}

export const applicationEntryRegularValidationSchema = () => ({
  name: validationSchema.textRequired(),
  phone: validationSchema.phone().required(),
  postal_code: validationSchema.postalCode().required(),
  prefecture: yup.string().trim().max(4).required(),
  city: validationSchema.textRequired(),
  town: validationSchema.textRequired(),
  street: validationSchema.textRequired(),
  building: validationSchema.text().nullable(),
  initial_support: yup.object().shape({
    is_request: yup.boolean().required(),
    request_datetime: yup.string().when('is_request', {
      is: true,
      then: yup.string().max(400).required(),
    }),
    email: yup.string().when('is_request', {
      is: true,
      then: validationSchema.email().required(),
    }),
  }),
  plan_category: validationSchema.textRequired(),
  plan: validationSchema.textRequired(),
  campaign_code: validationSchema.text().nullable(),
  is_active: yup.boolean(),
  is_applied_yadoc: yup.boolean(),
  introduced_partner: validationSchema.text().nullable(),
  admin_user: yup.object().shape({
    last_name: validationSchema.textRequired(),
    first_name: validationSchema.textRequired(),
    last_name_kana: validationSchema.textHiragana(),
    first_name_kana: validationSchema.textHiragana(),
    email: validationSchema.email().required(),
  }),
})

export const applicationEntryPartnerValidationSchema = () => ({
  partner_company_id: validationSchema
    .numberSelectEmptyStringToNull()
    .required(),
  branch_name: validationSchema.text().nullable(),
  contact_person_name: validationSchema.textRequired(),
  email: validationSchema.email().required(),
  phone: validationSchema.phone().required(),
  remarks: yup.string().trim().max(400).nullable(),
})

const ApplicationEntry: React.FC<ApplicationEntryProps> = ({
  partnerCompanies,
  defaultValues,
  onSubmit,
  entryPlans,
}: ApplicationEntryProps) => {
  const { t } = useTranslation()
  const { classes, cx } = applicationEntryStyles()
  const textFieldClasses = textFieldStyles().classes

  const schema = yup.object().shape({
    ...applicationEntryRegularValidationSchema(),
    partner: partnerCompanies
      ? yup.object().shape(applicationEntryPartnerValidationSchema())
      : yup.object(),
  })

  const sortedPartnerCompanies = partnerCompanies
    ? [...partnerCompanies].sort((a, b) => {
        const aName = a.name
        const bName = b.name
        if (aName < bName) {
          return -1
        }
        if (aName > bName) {
          return 1
        }
        return 0
      })
    : undefined

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    resetField,
    watch,
    formState: { isValid, errors },
  } = formMethods

  const watchAdminUserEmail: string | null = watch('admin_user.email')
  const watchInitialSupportIsRequest: boolean = watch(
    'initial_support.is_request'
  )

  const watchPlanCategory: string = watch('plan_category')
  const watchPlan: string = watch('plan')
  const watchPartner: string = watch('partner.partner_company_id')

  const [confirmed, setConfirmed] = useState(false)
  const [sending, setSending] = useState(false)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submitData, setSubmitData] = useState<ApplicationEntryFormProps>()

  const handleConfirm = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmed(event.target.checked)
  }

  // NOTE: 60日間スタートパッケージ希望可否を変更したときには下位の内容を毎回リセットする
  useEffect(() => {
    resetField('initial_support.email')
    resetField('initial_support.request_datetime')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchInitialSupportIsRequest])

  const SubmitModal: React.FC = () => (
    <Modal
      open={showSubmitModal}
      handleClose={() => {
        setShowSubmitModal(false)
      }}
    >
      <ConfirmContent
        onCancel={() => {
          setShowSubmitModal(false)
        }}
        onSubmit={() => {
          setSending(true)
          setShowSubmitModal(false)
          submitData &&
            onSubmit(submitData)
              .catch((error) => {
                console.log(error)
                if (
                  error instanceof ApiErrorWithMessage &&
                  error.has({
                    type: 'validation_error',
                    key: 'admin_user.email',
                    value: 'unique',
                  })
                ) {
                  setError('admin_user.email', {
                    type: 'validation_error',
                    message: t(
                      'validation.このメールアドレスはすでに登録されています'
                    ),
                  })
                }
              })
              .finally(() => {
                setSending(false)
              })
        }}
        title={
          <>
            {t(
              'メールアドレスに間違いがないかご確認ください。こちらで登録してよろしいですか？'
            )}
            <br />
            {submitData?.admin_user?.email}
          </>
        }
        submitLabel={t('登録')}
        sending={sending}
      />
    </Modal>
  )

  const SendingModal: React.FC = () => (
    <Modal open={sending} handleClose={() => {}}>
      <SendingContent />
    </Modal>
  )

  const submitHandler = (data: FieldValues) => {
    setSubmitData(data as ApplicationEntryFormProps)
    setShowSubmitModal(true)
  }

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <SubmitModal />
      <SendingModal />
      <Layout header={<AppBarCommon>{t('申込みフォーム')}</AppBarCommon>}>
        <Container maxWidth="md">
          <H2 className={classes.header}>{t('施設')}</H2>
          <div className={classes.category}>
            <StackItemTextField
              name="name"
              label={t('施設名')}
              placeholder={placeholder().clinic_name}
              errorMessage={errors?.name?.message}
              control={control}
              fullWidth
              required
            />
            <StackItemTextField
              name="phone"
              label={t('電話番号')}
              description={t('予約の問い合わせ先として使用されます。')}
              placeholder={placeholder().phone_number}
              errorMessage={errors?.phone?.message}
              control={control}
              fullWidth
              alignTop
              required
            />
            <FormProvider {...formMethods}>
              <MedAddressInputForm />
            </FormProvider>
            <StackItem
              label={
                <QuestionTooltipWithLabel label={t('YaDocのご利用')}>
                  {t(
                    '既に「オンライン診療システムYaDoc」のアカウントをお持ちの方は、チェックを入れてください。'
                  )}
                </QuestionTooltipWithLabel>
              }
            >
              <Text2 className={classes.text}>
                <Controller
                  name={'is_applied_yadoc'}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      className={classes.topCheckbox}
                      color="primary"
                    />
                  )}
                />
                {t('YaDocアカウントあり')}
              </Text2>
            </StackItem>
          </div>
          <H2 className={classes.header}>{t('管理者')}</H2>
          <div className={classes.category}>
            <StackItem label={t('氏名')} required>
              <span className={classes.firstInput}>
                <Controller
                  name={'admin_user.last_name'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      placeholder={placeholder().last_name}
                      error={!!errors?.admin_user?.last_name}
                      helperText={errors?.admin_user?.last_name?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </span>
              <Controller
                name={'admin_user.first_name'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    placeholder={placeholder().first_name}
                    error={!!errors?.admin_user?.first_name}
                    helperText={errors?.admin_user?.first_name?.message}
                    InputProps={{
                      className: cx(!field?.value && textFieldClasses.required),
                    }}
                  />
                )}
              />
            </StackItem>
            <StackItem label={t('氏名(ふりがな)')} required>
              <span className={classes.firstInput}>
                <Controller
                  name={'admin_user.last_name_kana'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      placeholder={placeholder().last_name_kana}
                      error={!!errors?.admin_user?.last_name_kana}
                      helperText={errors?.admin_user?.last_name_kana?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </span>
              <Controller
                name={'admin_user.first_name_kana'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    placeholder={placeholder().first_name_kana}
                    error={!!errors?.admin_user?.first_name_kana}
                    helperText={errors?.admin_user?.first_name_kana?.message}
                    InputProps={{
                      className: cx(!field?.value && textFieldClasses.required),
                    }}
                  />
                )}
              />
            </StackItem>
            <StackItemTextField
              name="admin_user.email"
              label={t('メールアドレス')}
              placeholder={placeholder().email}
              description={t(
                'ログイン、予約の通知、請求の連絡などに使用します。'
              )}
              errorMessage={errors?.admin_user?.email?.message}
              control={control}
              alignTop
              fullWidth
              required
            />
          </div>
          <H2 className={classes.header}>{t('申込みプラン')}</H2>
          <div className={classes.category}>
            <StackItem label={t('プラン選択')} alignTop={'32px'} required>
              <FormControl
                className={classes.formControl}
                error={!!(errors?.plan_category || errors?.plan)}
              >
                <Select
                  className={cx(
                    !watchPlanCategory && textFieldClasses.required
                  )}
                  defaultValue=""
                  variant="outlined"
                  displayEmpty
                  value={
                    watchPlanCategory && watchPlan
                      ? `${watchPlanCategory}:${watchPlan}`
                      : ''
                  }
                  onChange={(event) => {
                    const value: string = event.target.value as string
                    if (!value) {
                      setValue('plan_category', undefined as any, {
                        shouldValidate: true,
                      })
                      setValue('plan', undefined as any, {
                        shouldValidate: true,
                      })
                      return
                    }
                    const [category, plan] = value?.split(':') || [
                      undefined,
                      undefined,
                    ]
                    setValue('plan_category', category, {
                      shouldValidate: true,
                    })
                    setValue('plan', plan, { shouldValidate: true })
                  }}
                >
                  <MenuItem value="">{t('選択してください')}</MenuItem>
                  {entryPlans.map(({ category, plans }) => [
                    // NOTE: ListSubheaderとMenuItemが</>で結合できなかったので配列として返して結合している
                    <ListSubheader key={category}>{category}</ListSubheader>,
                    plans.map((plan) => (
                      <MenuItem
                        key={`${category}${plan}`}
                        value={`${category}:${plan}`}
                      >
                        {plan}
                      </MenuItem>
                    )),
                  ])}
                </Select>
                {!!(errors?.plan_category || errors?.plan) && (
                  <FormHelperText>
                    {t('プランを選択してください')}
                  </FormHelperText>
                )}
              </FormControl>
              <Controller
                name={'plan_category'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
              <Controller
                name={'plan'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
              <div className={classes.planDescription}>
                <Trans
                  i18nKey={
                    'trans.プラン詳細は<Link>こちら</Link>から確認できます。'
                  }
                  components={{
                    Link: (
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://support.yadoc.jp/manual/medical/quick/usage/"
                      />
                    ),
                  }}
                />
              </div>
            </StackItem>
            <StackItemTextField
              name="campaign_code"
              label={
                <QuestionTooltipWithLabel label={t('キャンペーンコード')}>
                  {t('キャンペーンコードをお持ちの場合は入力してください。')}
                </QuestionTooltipWithLabel>
              }
              placeholder={'00000'}
              errorMessage={errors?.campaign_code?.message}
              control={control}
            />
          </div>
          <H2 className={classes.header}>{t('導入サポート')}</H2>
          <div className={classes.category}>
            <StackItem
              label={t('60日間スタートパッケージ')}
              required
              description={
                <Trans
                  i18nKey={
                    'trans.初期設定やご使用方法に関して、お電話もしくはZoomを使用してのサポートを実施しております。...'
                  }
                />
              }
              alignTop
            >
              <div className={classes.isRequestSwitch}>
                <FormProvider {...formMethods}>
                  <SwitchRadioButton
                    name={'initial_support.is_request'}
                    falseLabel={t('希望しない')}
                    trueLabel={t('希望する')}
                  />
                </FormProvider>
              </div>
            </StackItem>
            {watchInitialSupportIsRequest && (
              <>
                <StackItemTextField
                  name="initial_support.request_datetime"
                  label={t('希望初回日時')}
                  required
                  placeholder={t(
                    'placeholder.・○月○日（月）14:00〜16:00\n・○月○日（水）12:30〜15:30\n・○月○日（金）10:00〜13:00'
                  )}
                  description={
                    <>
                      {t(
                        '平日10:00〜17:00の範囲内で、ご希望の日時を3つご提示ください。'
                      )}
                      <br />
                      {t(
                        '※上記以外の時間をご希望の場合も、お気軽にご相談ください。'
                      )}
                    </>
                  }
                  errorMessage={
                    errors?.initial_support?.request_datetime?.message
                  }
                  control={control}
                  alignTop
                  multiline
                  rows={6}
                  textCounter={400}
                  fullWidth
                />
                <StackItem
                  label={t('連絡用メールアドレス')}
                  required
                  description={t(
                    'サポート当日のZoomのURLや、当日までにご用意いただきたい事項の案内をいたします。'
                  )}
                  alignTop
                >
                  <Box display="flex">
                    <span className={classes.firstInput}>
                      <Controller
                        name={'initial_support.email'}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            InputProps={{
                              className: cx(
                                !field?.value && textFieldClasses.required
                              ),
                            }}
                            className={classes.initialSupportEmail}
                            variant="outlined"
                            placeholder={placeholder().email}
                            error={!!errors?.initial_support?.email}
                            helperText={errors?.initial_support?.email?.message}
                          />
                        )}
                      />
                    </span>
                    <Button
                      style={{ alignSelf: 'center' }}
                      variant="contained"
                      color="primary"
                      disabled={!watchAdminUserEmail}
                      onClick={() => {
                        watchAdminUserEmail &&
                          setValue(
                            'initial_support.email',
                            watchAdminUserEmail,
                            { shouldValidate: true }
                          )
                      }}
                    >
                      {t('管理者メールアドレスからコピー')}
                    </Button>
                  </Box>
                </StackItem>
              </>
            )}
          </div>
          <Controller
            name={'is_active'}
            render={({ field }) => <input {...field} type="hidden" />}
            control={control}
          />
          <H2 className={classes.header}>{t('代理店')}</H2>
          <div className={classes.category}>
            {!partnerCompanies ? (
              <StackItemTextField
                name="introduced_partner"
                label={t('紹介代理店名')}
                errorMessage={errors?.introduced_partner?.message}
                control={control}
                fullWidth
              />
            ) : (
              <>
                <StackItem label={t('企業名')} required>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    error={!!errors?.partner?.partner_company_id}
                  >
                    <Controller
                      name={'partner.partner_company_id'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          variant="outlined"
                          className={cx(
                            !watchPartner && textFieldClasses.required
                          )}
                        >
                          <MenuItem value="">{t('選択してください')}</MenuItem>
                          {sortedPartnerCompanies?.map((o) => (
                            <MenuItem key={`${o.name}${o.id}`} value={o.id}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors?.partner?.partner_company_id && (
                      <FormHelperText>
                        {errors?.partner?.partner_company_id?.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </StackItem>
                <StackItemTextField
                  name="partner.branch_name"
                  label={t('部署・支店名')}
                  placeholder={placeholder().partner_branch_name}
                  errorMessage={errors?.partner?.branch_name?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.contact_person_name"
                  label={t('担当者名')}
                  required
                  placeholder={placeholder().account_name}
                  errorMessage={errors?.partner?.contact_person_name?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.email"
                  label={t('メールアドレス')}
                  required
                  placeholder={placeholder().email}
                  errorMessage={errors?.partner?.email?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.phone"
                  label={t('電話番号')}
                  required
                  placeholder={placeholder().phone_number}
                  errorMessage={errors?.partner?.phone?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.remarks"
                  label={t('備考')}
                  errorMessage={errors?.partner?.remarks?.message}
                  control={control}
                  alignTop
                  fullWidth
                  multiline
                />
              </>
            )}
          </div>

          <div className={classes.submitWrapper}>
            <Confirmation
              checked={confirmed}
              onCheckboxClick={handleConfirm}
              termsUrl={information.med.terms}
              privacyPolicyUrl={information.med.privacyPolicy}
              serviceUseUrl={information.med.serviceUse}
            />
            <Button
              className={cx(classes.submitButton)}
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || !confirmed || !!Object.keys(errors).length}
            >
              {t('送信')}
            </Button>
          </div>
        </Container>
      </Layout>
    </form>
  )
}

export default ApplicationEntry
